import * as Yup from 'yup';
import { useFormik } from 'formik';
import { refreshTokenRef, tokenRef } from 'common/constant';
import { Navigate, useNavigate } from 'react-router-dom';
import TextInput from 'components/ui/TextInput';
import { useLocalStorage } from 'hooks/useStorage';
import PrimaryButton from 'components/ui/PrimaryButton';
import FormikValidationError from 'components/ui/FormikErrors';
import { useLoginMutation } from 'redux/reducers/auth';
import { SCREENS_ROUTES_ENUMS } from 'common/enum';
import { toast } from 'react-toastify';
import { useContext, useState } from 'react';
import { useAdminLogin } from 'service/auth.query';
import { ConfigurationContext } from 'components/provider/OrginazationContext';
import { backendURL } from 'redux/reducers/settings';
import FallBackLoader from 'components/ui/FallBackLoader';

const LoginSchema = Yup.object().shape({
  password: Yup.string().min(2, 'Too Short!').max(50, 'Too Long!').required('Password is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  user_type: Yup.number().default(1)
});

const Login = () => {
  let navigate = useNavigate();
  // Read token only for the "already logged in" guard — do NOT use setToken
  // after login, because calling setToken triggers a React-state re-render that
  // fires the <Navigate to="/" /> below before App.tsx has the correct routes.
  const { value: token } = useLocalStorage(tokenRef);
  const [passwordToogle, setPasswordToogle] = useState('password');
  // Full-screen loader shown after a successful login while the page reloads
  const [redirecting, setRedirecting] = useState(false);

  const togglePassword = () => {
    if (passwordToogle === 'password') {
      setPasswordToogle('text');
      return;
    }
    setPasswordToogle('password');
  };

  const { mutate: adminLogin, isLoading } = useAdminLogin();
  const formik = useFormik({
    validationSchema: LoginSchema,
    initialValues: {
      email: '',
      password: ''
    },
    onSubmit: (values) => {
      const payload = {
        email: values.email,
        password: values.password,
        user_type: 1
      };
      adminLogin(payload, {
        onSuccess: (res) => {
          if (res?.data.code == 200) {
            toast.success(res?.data?.message);
            // Write directly and synchronously to localStorage so the token is
            // guaranteed to be present before location.reload() fires.
            window.localStorage.setItem(tokenRef, JSON.stringify(res?.data.data?.access));
            window.localStorage.setItem(
              refreshTokenRef,
              JSON.stringify(res?.data.data?.refresh)
            );
            // Show the loader, then reload so App.tsx picks up the correct role.
            setRedirecting(true);
            setTimeout(() => location.reload(), 800);
          }
        },
        onError: (err: any) => {
          toast.error(err.error?.data?.message);
        }
      });
    }
  });

  // Already authenticated — navigate directly (no flash risk here since the
  // token was stored on a previous session).
  if (token) {
    return <Navigate to="/" replace={true} />;
  }

  // Show full-screen loader while the page is about to reload after login.
  if (redirecting) {
    return <FallBackLoader />;
  }

  const { appConfig } = useContext(ConfigurationContext);

  return (
    <>
      <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
        <div className="bg-overlay" />
        <div className="auth-page-content overflow-hidden pt-lg-5">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="card overflow-hidden">
                  <div className="row g-0">
                    <div className="col-lg-6">
                      <div className="p-lg-5 p-4 auth-one-bg h-100 position-relative overflow-hidden">
                        {appConfig?.register_image && (
                          <img
                            src={backendURL + appConfig.register_image}
                            alt="auth-bg"
                            style={{
                              position: 'absolute',
                              justifyContent: 'center',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              // objectFit: 'cover',
                              zIndex: 0
                            }}
                          />
                        )}
                        <div className="bg-overlay" />
                        <div className="position-relative h-100 d-flex flex-column">
                          <h2 className="text-white text-center mb-4 fw-bold position-absolute top-0 start-50 translate-middle-x mt-4">
                            {appConfig?.title ?? ''}
                          </h2>

                          <div className="mt-auto">
                            <div className="mb-3">
                              <i className="ri-double-quotes-l display-4 text-success" />
                            </div>

                            <div
                              id="qoutescarouselIndicators"
                              className="carousel slide"
                              data-bs-ride="carousel">
                              <div className="carousel-indicators bg-red">
                                <button
                                  type="button"
                                  data-bs-target="#qoutescarouselIndicators"
                                  data-bs-slide-to={0}
                                  className="active"
                                />
                                <button
                                  type="button"
                                  data-bs-target="#qoutescarouselIndicators"
                                  data-bs-slide-to={1}
                                />
                                <button
                                  type="button"
                                  data-bs-target="#qoutescarouselIndicators"
                                  data-bs-slide-to={2}
                                />
                              </div>

                              <div className="carousel-inner text-center text-white pb-5">
                                <div className="carousel-item active">
                                  <p className="fs-15 fst-italic">
                                    " Create Assignment, Courses and Training "
                                  </p>
                                </div>
                                <div className="carousel-item">
                                  <p className="fs-15 fst-italic">
                                    " Assign Coordinator and Evaluator to the routine "
                                  </p>
                                </div>
                                <div className="carousel-item">
                                  <p className="fs-15 fst-italic">
                                    " Add trainee, coordinator and evaluator in the system "
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="p-lg-5 p-4">
                        <div>
                          <h5 className="text-primary">Welcome Back !</h5>
                          <p>Sign in and continue to Training Management System.</p>
                        </div>
                        <div className="mt-4">
                          <form className="" onSubmit={formik.handleSubmit}>
                            <TextInput
                              label="Email"
                              placeholder="Enter email"
                              containerClassName="mb-3"
                              name="email"
                              type="email"
                              onChange={formik.handleChange}
                              value={formik.values.email}
                              onBlur={formik.handleBlur}
                              formik={formik}
                            />

                            <div>
                              <label className="form-label">Password</label>
                              <div className="position-relative auth-pass-inputgroup">
                                <input
                                  name="password"
                                  type={passwordToogle}
                                  className="form-control pe-5"
                                  placeholder="Enter password"
                                  id="password-input"
                                  onChange={formik.handleChange}
                                  value={formik.values.password}
                                  onBlur={formik.handleBlur}
                                />
                                <button
                                  aria-label="view password"
                                  className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                  type="button"
                                  id="password-addon">
                                  <i
                                    className="ri-eye-fill align-middle"
                                    onClick={togglePassword}></i>
                                </button>
                              </div>
                            </div>

                            <FormikValidationError
                              name="password"
                              errors={formik.errors}
                              touched={formik.touched}
                            />
                            <div className="form-check d-flex align-items-center justify-content-between">
                              <p
                                onClick={() => navigate(SCREENS_ROUTES_ENUMS.FORGOT_PASSWORD)}
                                className=" btn">
                                Forgot password?
                              </p>
                            </div>
                            <div className="hstack gap-2 justify-center mt-4">
                              <PrimaryButton
                                title="Login"
                                type="submit"
                                className="w-100"
                                isLoading={isLoading}
                              />
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="footer">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="text-center">
                  <p className="mb-0">© All Right Reserved</p>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Login;
